import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLang, useTranslation } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Menu, User, LogOut, LayoutDashboard, MessageSquare, Plus, ShieldCheck, Package, Sun, Moon, Download } from 'lucide-react';
import { usePWAInstall } from '@/hooks/usePWAInstall';

export default function Header() {
  const { user, logout, unreadCount } = useAuth();
  const { lang, switchLang } = useLang();
  const { tc } = useTranslation('header');
  const { t } = useTranslation('header');
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { canInstall, install } = usePWAInstall();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/annonces', label: tc('listings') },
    { href: '/comment-ca-marche', label: tc('howItWorks') },
    { href: '/service-publication', label: tc('turnkeyService') },
    { href: '/a-propos', label: tc('about') },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white dark:bg-[#111827] border-b border-neutral-200 dark:border-neutral-700" data-testid="header">
      <div className="site-container">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
            <div className="w-8 h-8 rounded-md bg-[#1F3F72] flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold text-[#1F3F72] dark:text-white">DestockPro</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="px-3 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:text-[#1F3F72] dark:hover:text-[#F25C05] transition-colors rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-800"
                data-testid={`nav-${link.href.slice(1)}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-neutral-500 hover:text-[#1F3F72] dark:text-neutral-400 dark:hover:text-[#F25C05] hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Language Switcher */}
            <div className="flex items-center border dark:border-neutral-600 rounded-md overflow-hidden" data-testid="lang-switcher">
              <button
                onClick={() => switchLang('fr')}
                className={`px-2 py-1.5 text-xs font-semibold transition-colors ${lang === 'fr' ? 'bg-[#1F3F72] text-white' : 'bg-white dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-700'}`}
                data-testid="lang-fr-btn"
              >
                FR
              </button>
              <button
                onClick={() => switchLang('nl')}
                className={`px-2 py-1.5 text-xs font-semibold transition-colors ${lang === 'nl' ? 'bg-[#1F3F72] text-white' : 'bg-white dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-700'}`}
                data-testid="lang-nl-btn"
              >
                NL
              </button>
            </div>

            {user ? (
              <>
                <Button
                  size="sm"
                  className="bg-[#F25C05] hover:bg-[#d94f04] text-white"
                  onClick={() => navigate('/publier')}
                  data-testid="header-publish-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> {t('publishAd')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/messages')}
                  className="relative"
                  data-testid="header-messages-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4.5 h-4.5 min-w-[18px] rounded-full bg-[#F25C05] text-white text-[10px] font-bold flex items-center justify-center" data-testid="unread-badge">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="header-user-menu">
                      <User className="w-4 h-4 mr-1" />
                      {user.first_name}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/tableau-de-bord')} data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> {tc('dashboard')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="menu-messages">
                      <MessageSquare className="w-4 h-4 mr-2" /> {tc('messages')}
                      {unreadCount > 0 && (
                        <span className="ml-auto bg-[#F25C05] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{unreadCount}</span>
                      )}
                    </DropdownMenuItem>
                    {user.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                        <ShieldCheck className="w-4 h-4 mr-2" /> {tc('admin')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" /> {tc('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate('/connexion')} data-testid="header-login-btn">
                  {tc('login')}
                </Button>
                <Button
                  size="sm"
                  className="bg-[#F25C05] hover:bg-[#d94f04] text-white"
                  onClick={() => navigate('/inscription')}
                  data-testid="header-register-btn"
                >
                  {tc('signup')}
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="flex items-center gap-2 lg:hidden">
            {/* Mobile Language Switcher */}
            <div className="flex items-center border rounded-md overflow-hidden" data-testid="lang-switcher-mobile">
              <button
                onClick={() => switchLang('fr')}
                className={`px-1.5 py-1 text-[10px] font-semibold transition-colors ${lang === 'fr' ? 'bg-[#1F3F72] text-white' : 'bg-white text-neutral-500'}`}
              >
                FR
              </button>
              <button
                onClick={() => switchLang('nl')}
                className={`px-1.5 py-1 text-[10px] font-semibold transition-colors ${lang === 'nl' ? 'bg-[#1F3F72] text-white' : 'bg-white text-neutral-500'}`}
              >
                NL
              </button>
            </div>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 pt-10 bg-white dark:bg-[#111827]">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <nav className="flex flex-col gap-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="px-3 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-md"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="border-t my-3" />
                  {user ? (
                    <>
                      <Link to="/tableau-de-bord" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-md">
                        {tc('dashboard')}
                      </Link>
                      <Link to="/messages" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-md flex items-center justify-between">
                        {tc('messages')}
                        {unreadCount > 0 && (
                          <span className="bg-[#F25C05] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{unreadCount}</span>
                        )}
                      </Link>
                      <Link to="/publier" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium text-[#F25C05] hover:bg-orange-50 rounded-md">
                        {tc('publish')}
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/admin" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-md">
                          {tc('admin')}
                        </Link>
                      )}
                      {canInstall && (
                        <>
                          <div className="border-t my-3" />
                          <button
                            onClick={async () => { await install(); setMobileOpen(false); }}
                            className="px-3 py-2.5 text-sm font-medium text-[#1F3F72] dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md text-left flex items-center gap-2.5"
                            data-testid="install-pwa-btn"
                          >
                            <Download className="w-4 h-4" /> {tc('installApp')}
                          </button>
                        </>
                      )}
                      <div className="border-t my-3" />
                      <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md text-left">
                        {tc('logout')}
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/connexion" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-md">
                        {tc('login')}
                      </Link>
                      <Link to="/inscription" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 text-sm font-medium text-[#F25C05] hover:bg-orange-50 rounded-md">
                        {tc('signup')}
                      </Link>
                      {canInstall && (
                        <>
                          <div className="border-t my-3" />
                          <button
                            onClick={async () => { await install(); setMobileOpen(false); }}
                            className="px-3 py-2.5 text-sm font-medium text-[#1F3F72] dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md text-left flex items-center gap-2.5"
                            data-testid="install-pwa-btn-guest"
                          >
                            <Download className="w-4 h-4" /> {tc('installApp')}
                          </button>
                        </>
                      )}
                    </>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
